import { createRef, Component } from 'react';
import styled from 'styled-components';
import { Tags } from './tags';
import { deleteUnusedTagsDialog } from './deleteUnusedTagsDialog';
import { SettingsBar } from '@ardoq/settings-bar';
import { type SettingsConfig, SettingsType } from '@ardoq/view-settings';
import {
  dispatchAction,
  reducedStream,
  ExtractPayload,
  reducer,
} from '@ardoq/rxbeach';
import { deleteTags, showAddTagDialog } from 'streams/tags/TagActions';
import { ArdoqId, ViewIds } from '@ardoq/api-types';
import { IconName } from '@ardoq/icons';
import { getRightMenuConfig } from 'viewSettings/getRightMenuConfig';
import { addToPresentation } from 'viewSettings/exportHandlers';
import { exportHtmlToPng, getExportProfilingTransaction } from '@ardoq/export';
import { getExportLabels } from 'views/getExportLabels';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { logError } from '@ardoq/logging';
import {
  TagModel,
  TagscapeViewProperties,
  TagscapeViewSettings,
} from './types';
import { colors, s8 } from '@ardoq/design-tokens';
import { trackExportingVisualization } from 'tracking/events/visualizations';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { InteractionState, toProps } from './viewModel$';
import { tagScapeContextMenuName } from './contextMenu';
import { getStyleSheets } from 'tabview/getSharedExportFunctions';
import { EmptyState as EmptyStateBase } from './EmptyState';
import { EXCLUDE_UNUSED_TAGS } from 'tabview/consts';
import { tagscapeCommands } from './commands';
import { combineLatest, map } from 'rxjs';
import { tags$ } from 'streams/tags/tags$';
import { context$ } from 'streams/context/context$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import {
  afterDragStart,
  beforeDragEnd,
} from 'navigator2024/handlers/dragndrop';
import {
  focusedComponentChanged,
  hoveredComponentChanged,
} from 'tabview/actions';

import { HasViewInstanceId } from '@ardoq/graph';
import { WorkspaceModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';
import { getWorkspaceModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';

const VIEW_ID = ViewIds.TAGSCAPE;

const toCurrentSettings = ({
  currentSettings,
}: WorkspaceModeInstanceViewSettings<ViewIds.TAGSCAPE>) => currentSettings;

const DragDropComp = styled.p`
  color: color-mix(in srgb, ${colors.grey900} 80%, transparent);
  font-style: italic;
  margin: 0;
  padding: ${s8} 12px 0;
`;
interface LeftMenuItem {
  id: keyof TagscapeViewSettings | 'addtag' | 'deleteUnused';
  label: string;
  iconName: IconName;
  type: SettingsType;
  isDisabled?: boolean;
  isActive?: boolean;
  onClick?: (tags: TagModel[]) => void;
}

const deleteUnused = (unusedTags: TagModel[]) => {
  const cids = unusedTags.map(tag => tag.cid);
  dispatchAction(deleteTags({ cids }));
};

const buildLeftMenu = (
  i18nLocale: I18nLocale,
  rootWorkspace: ArdoqId
): LeftMenuItem[] => [
  {
    id: 'addtag',
    label: t(i18nLocale, 'tabview:tagscape.menu.addTag', 'Add tag'),
    iconName: IconName.ADD_CIRCLE,
    type: SettingsType.BUTTON,
    isDisabled: false,
    onClick: () => dispatchAction(showAddTagDialog({ rootWorkspace })),
  },
  {
    id: 'excludeUnused',
    label: t(
      i18nLocale,
      'tabview:tagscape.menu.excludeUnusedTags',
      EXCLUDE_UNUSED_TAGS
    ),
    iconName: IconName.TAG,
    isActive: false,
    type: SettingsType.TOGGLE,
  },
  {
    id: 'deleteUnused',
    label: t(
      i18nLocale,
      'tabview:tagscape.menu.deleteUnusedTags',
      'Delete unused tags'
    ),
    iconName: IconName.CLOSE,
    type: SettingsType.BUTTON,
    isDisabled: false,
    onClick: deleteUnused,
  },
  {
    id: 'excludeRefs',
    label: t(
      i18nLocale,
      'tabview:tagscape.menu.excludeReferences',
      'Exclude references'
    ),
    iconName: IconName.RANDOM,
    isActive: false,
    type: SettingsType.TOGGLE,
  },
  {
    id: 'excludeComps',
    label: t(
      i18nLocale,
      'tabview:tagscape.menu.excludeComponents',
      'Exclude components'
    ),
    iconName: IconName.FILE_OUTLINED,
    isActive: false,
    type: SettingsType.TOGGLE,
  },
  {
    id: 'count',
    label: t(
      i18nLocale,
      'tabview:tagscape.menu.countReferencesAndComponents',
      'Count references and components'
    ),
    iconName: IconName.CALCULATOR,
    isActive: false,
    type: SettingsType.TOGGLE,
  },
];

type GetLeftMenuArgs = Pick<TagscapeViewProperties, 'viewState' | 'viewModel'> &
  WithI18nLocale;

const getLeftMenu = (
  oldMenu: LeftMenuItem[],
  { viewState, viewModel, i18nLocale }: GetLeftMenuArgs
): SettingsConfig[] =>
  oldMenu.map((menuItem): SettingsConfig => {
    if (menuItem.id === 'deleteUnused') {
      const unusedTags = viewModel.tags.filter(tag => !tag.isUsed);
      const isDisabled = !unusedTags.length;

      return {
        ...menuItem,
        isDisabled,
        onClick: async () => {
          if (await deleteUnusedTagsDialog(unusedTags, i18nLocale)) {
            deleteUnused(unusedTags);
          }
        },
        popoverText: isDisabled
          ? t(
              i18nLocale,
              'tabview:tagscape.menu.noUnusedTagsToDelete',
              'No unused tags to delete in this workspace'
            )
          : undefined,
      };
    }
    if (menuItem.type === SettingsType.TOGGLE) {
      return {
        ...menuItem,
        isActive: viewState[menuItem.id as keyof TagscapeViewSettings],
        onViewSettingsUpdate,
      };
    }
    if (menuItem.type === SettingsType.BUTTON) {
      return {
        ...menuItem,
        isDisabled: menuItem.isDisabled || !viewModel.workspace.isWriter,
        onClick: menuItem.onClick as () => void,
      };
    }
    return menuItem as SettingsConfig;
  });

const TagHelp = ({ i18nLocale }: WithI18nLocale) => (
  <DragDropComp>
    {t(
      i18nLocale,
      'tabview:tagscape.help',
      'Tags in the selected context workspace appear here. Drag components in that workspace from the navigator to tag them. Select a different workspace to see and tag components in it.'
    )}
  </DragDropComp>
);
export class TagscapeView extends Component<TagscapeViewProperties> {
  private contentPaneRef = createRef<HTMLDivElement>();
  exportToPng = () => {
    const transaction = getExportProfilingTransaction();

    exportHtmlToPng({
      transaction,
      container: this.contentPaneRef.current,
      exportedViewMetadata: {
        name: VIEW_ID,
        isViewpointMode: false,
      },
      trackingFunction: trackExportingVisualization,
      getStyleSheets,
      labels: getExportLabels(this.props.i18nLocale),
    });
  };
  render() {
    const { viewModel, viewState, i18nLocale } = this.props;
    if (!viewModel) {
      logError(Error('Invalid arguments in Tagscape View.'));
      return null;
    }
    const {
      tags,
      visibleTags,
      hasExcludedTags,
      isEmptyView,
      disableAddingTagToDraggedComponent,
    } = viewModel;

    return (
      <div
        className="tab-pane tabtagscape tabscapeTab active"
        data-context-menu={tagScapeContextMenuName}
        onClick={() => tagscapeCommands.setFocusedItemState(null)}
      >
        <div className="menuContainer">
          <SettingsBar
            viewId={VIEW_ID}
            leftMenu={getLeftMenu(
              buildLeftMenu(i18nLocale, viewModel.workspace.id),
              {
                viewModel,
                viewState,
                i18nLocale,
              }
            )}
            rightMenu={getRightMenuConfig({
              isViewpointMode: false,
              viewId: VIEW_ID,
              viewstate: viewState,
              exports: {
                exportToPng: this.exportToPng,
                addToPresentation: () => addToPresentation(VIEW_ID, false),
                isDisabled: isEmptyView,
              },
              withLegend: false,
              knowledgeBaseLink: KnowledgeBaseLink.TAGSCAPE,
              onViewSettingsUpdate,
              i18nLocale,
            })}
            i18nLocale={i18nLocale}
          />
        </div>
        {isEmptyView ? (
          <EmptyStateBase
            hasNoTags={!tags.length}
            hasExcludedTags={hasExcludedTags}
          />
        ) : (
          <>
            <TagHelp i18nLocale={i18nLocale} />
            <div ref={this.contentPaneRef} className="contentPane">
              <Tags
                tags={visibleTags}
                viewState={viewState}
                disabled={disableAddingTagToDraggedComponent}
                i18nLocale={i18nLocale}
              />
            </div>
          </>
        )}
      </div>
    );
  }
}

export const emptyInteractionState: InteractionState = {
  hoveredComponentId: null,
  focusedComponentId: null,
  draggedIds: null,
  disableAddingTagToDraggedComponent: false,
};

const handleDragStarted = (
  state: InteractionState,
  { ids }: ExtractPayload<typeof afterDragStart>
): InteractionState => ({
  ...state,
  draggedIds: ids,
  disableAddingTagToDraggedComponent: false,
});

const handleDragEnded = (state: InteractionState): InteractionState => ({
  ...state,
  draggedIds: null,
  disableAddingTagToDraggedComponent: false,
});

const handleHoveredComponentChanged = (
  state: InteractionState,
  hoveredComponentId: ArdoqId | null
): InteractionState => ({
  ...state,
  hoveredComponentId,
});

const handleFocusedComponentChanged = (
  state: InteractionState,
  focusedComponentId: ArdoqId | null
): InteractionState => ({
  ...state,
  focusedComponentId,
});

const interactionState$ = reducedStream(
  'tagscapeInteractionState',
  emptyInteractionState,
  [
    reducer(afterDragStart, handleDragStarted),
    reducer(beforeDragEnd, handleDragEnded),
    reducer(hoveredComponentChanged, handleHoveredComponentChanged),
    reducer(focusedComponentChanged, handleFocusedComponentChanged),
  ]
);

const ConnectedTagscapeView = connectInstanceWithProps(
  TagscapeView,
  ({ viewInstanceId }: HasViewInstanceId) =>
    combineLatest({
      interactionState: interactionState$,
      tags: tags$,
      context: context$,
      permissionContext: currentUserPermissionContext$,
      activeScenario: activeScenario$,
      viewState: getWorkspaceModeViewSettingsWithChanges$(
        VIEW_ID,
        viewInstanceId
      ).pipe(map(toCurrentSettings)),
      i18nLocale: i18nLocale$,
    }).pipe(map(toProps))
);

export { ConnectedTagscapeView };
